import React from 'react';
import * as CryptoJS from 'crypto-js';
import User from 'src/models/User';
import { PLAN_NAME } from 'src/pages/constants';
import {
  MealPlanVariant,
  Plan,
  WeeksPurchased,
} from 'src/pages/deprecated/payment/types';
import { AxiosError } from 'axios';

export function dedupeObjectArray<T extends { [accessor: string]: any }>(
  objectList: Array<T>,
  accessor: string
): Array<T> {
  const deduped: Array<T> = [];
  objectList.forEach((item: T) => {
    if (
      !deduped.find((predicate: T) => predicate[accessor] === item[accessor])
    ) {
      deduped.push(item);
    }
  });
  return deduped;
}

export function findObjectInArray<T extends { [accessor: string]: any }>(
  predicate: any,
  objArray: Array<T>,
  accessor: string
): T | undefined {
  return objArray && objArray.find((item: T) => item[accessor] === predicate);
}

export function sortObjectListByAccessor<T extends { [accessor: string]: any }>(
  objectList: Array<T>,
  accessor: string
) {
  return objectList.sort((a, b) => {
    const varA = a[accessor].toLowerCase
      ? a[accessor].toLowerCase()
      : a[accessor];
    const varB = b[accessor].toLowerCase
      ? b[accessor].toLowerCase()
      : b[accessor];

    if (varA < varB) return -1;
    if (varA > varB) return 1;
    return 0;
  });
}

export function pushToArray(arr: any[], obj: { id: any }) {
  const newArr = arr;
  const index = arr.findIndex(e => e.id === obj.id);

  if (index === -1) {
    newArr.push(obj);
  } else {
    newArr[index] = obj;
  }
  return newArr;
}

export const getRecommendedSubscription = (
  scale: string,
  challenge: string, // 0 means standard, 1 means premium
  feeling: string, // 0 means standard, 1 means premium
  support: string, // 0 means standard, 1 means premium
  country: string, // 0 means standard, 1 means premium
  availablePlans: any
) => {
  if (!!availablePlans) {
    const months = parseInt(scale) >= 7 ? 6 : 3;
    let standardCount = 0;
    let premiumCount = 0;
    const plans = [];
    let finalPlanId = '';
    if (parseInt(challenge) > 0) {
      ++premiumCount;
    } else {
      ++standardCount;
    }
    if (parseInt(feeling) > 0) {
      ++premiumCount;
    } else {
      ++standardCount;
    }
    if (parseInt(support) > 0) {
      ++premiumCount;
    } else {
      ++standardCount;
    }

    if (premiumCount > standardCount || country !== 'Australia') {
      for (const key in availablePlans) {
        if (key.includes('Premium')) {
          plans.push(key);
        }
      }
    } else {
      for (const key in availablePlans) {
        if (key.includes('Standard')) {
          plans.push(key);
        }
      }
    }

    plans.forEach(item => {
      if (item.includes(months.toString())) {
        finalPlanId = item;
      }
    });
    return finalPlanId;
  }
};

export const getRecommendedSubscriptionSignup = (
  scale: string,
  challenge: string, // 0 means standard, 1 means premium
  feeling: string, // 0 means standard, 1 means premium
  support: string, // 0 means standard, 1 means premium
  country: string, // 0 means standard, 1 means premium
  availablePlans: Plan[]
) => {
  if (availablePlans) {
    const months = parseInt(scale) >= 7 ? 6 : 3;
    const plans: string[] = [];
    let finalPlanId = '';

    // Always filter for Standard plans only - Premium plans are no longer available
    availablePlans.forEach(plan => {
      if (plan.id?.includes('Standard')) {
        plans.push(plan.id);
      }
    });

    plans.forEach(item => {
      if (item.includes(months.toString())) {
        finalPlanId = item;
      }
    });
    return finalPlanId;
  }
};

export const useViewport = () => {
  const [width, setWidth] = React.useState(window.innerWidth);
  const mobileWidth = 576;
  const desktopWidth = 1440;

  React.useEffect(() => {
    const handleWindowResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handleWindowResize);
    return () => window.removeEventListener('resize', handleWindowResize);
  }, []);

  return {
    isMobile: width < mobileWidth,
    isDesktop: width >= desktopWidth,
    width,
  };
};

export const isChallengeBundleActive = (): boolean => {
  return (
    typeof sessionStorage !== 'undefined' &&
    sessionStorage.getItem('isChallengeBundleActive') === 'true'
  );
};

export const isUserFromAustralia = (params?: {
  blockChallengeBundleCheck?: boolean;
}) => {
  const { blockChallengeBundleCheck = false } = params ?? {};
  const searchParams = new URLSearchParams(window.location.search);
  if (
    (searchParams.has('challengeBundle') || isChallengeBundleActive()) &&
    !blockChallengeBundleCheck
  ) {
    return true;
  }
  const showStandardPlan = searchParams.get('showStandardPlan'); //to bypass the condition to check for user country and show standard plan

  if (process.env.NODE_ENV !== 'production' || showStandardPlan) {
    return true; // Apply this to production only to avoid checking standard plan for offshore developers
  }
  const country1 = searchParams.get('country');
  const country2 = searchParams.get('coutntry'); // this should be removed once we update the typeform field
  const country = country2 ?? country1;

  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return (
    userTimezone.includes('Australia') || country?.toLowerCase() === 'australia'
  );
};

export const isUserFromNewZealand = () => {
  const searchParams = new URLSearchParams(window.location.search);
  if (searchParams.has('challengeBundle') || isChallengeBundleActive()) {
    return false;
  }

  const country = searchParams.get('country');

  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // checks for the timezone falling under NZ
  const isNZUser =
    userTimezone.includes('Auckland') ||
    userTimezone.includes('Chatham') ||
    country === 'New Zealand';

  return isNZUser;
};

export const isUserFromUK = () => {
  const searchParams = new URLSearchParams(window.location.search);
  if (searchParams.has('challengeBundle') || isChallengeBundleActive()) {
    return false;
  }

  const country = searchParams.get('country');

  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // checks for the timezone falling under UK (IANA timezone commonly: Europe/London)
  const isUKUser =
    userTimezone.includes('Europe/London') ||
    userTimezone.includes('London') ||
    country === 'United Kingdom' ||
    country === 'UK' ||
    country === 'Great Britain';

  return isUKUser;
};

export type FinalPlanObject = {
  id: string;
  productId: string;
  weeklyMealPlanQty: number;
  unitAmountCents: number;
  unitAmount: string;
  unitAmountPerWeek: number;
  unitAmountPerDay: number;
  weeksPurchased: WeeksPurchased;
  interval: string;
  intervalCount: number;
  perCycleAmount: string | number;
};

export const getFullPlanObj = (
  availablePlans: Plan[],
  selectedPlanId: string,
  addonWeek: number
): FinalPlanObject | undefined => {
  const planObj = availablePlans.find((plan: Plan) => {
    return plan.id === selectedPlanId;
  });
  const unitAmountCents =
    (planObj?.mealPlanVariants &&
      planObj?.mealPlanVariants[addonWeek]?.unitAmountCents) ||
    0;
  const intervalCount = planObj?.intervalCount || 1;
  const perCycleAmount = unitAmountCents / 100 / intervalCount || 0;
  let er = /^-?[0-9]+$/;

  return (
    planObj && {
      interval: planObj.interval,
      intervalCount: planObj?.intervalCount,
      perCycleAmount: er.test(perCycleAmount.toString())
        ? perCycleAmount
        : perCycleAmount.toFixed(2),
      ...planObj?.mealPlanVariants[addonWeek],
    }
  );
};

export const PLAN_NAME_OBJ: any = {
  '1 week': 'Taster',
  '1 month': 'Kickstart',
  '2 month': 'Kickstart',
  '3 month': 'Elevate',
  '6 month': 'Transform',
  '12 month': 'Elevate',
};

export const returnFilteredPlans = (planList: Plan[]) => {
  // NOTE: No Premium Plan is available anymore
  // Always filter for Standard plans only
  const filteredPlans = planList.filter(plan =>
    PLAN_NAME.STANDARD_PLAN.includes(plan.id)
  );

  return filteredPlans;
};

/**
 * Formats interval display string, showing "Annual" for 12 months,
 * "Monthly" for 1 month, otherwise the normal format.
 * @param intervalCount - The number of intervals
 * @param interval - The interval type (e.g., 'month', 'week')
 * @returns Formatted string (e.g., "Annual" for 12 months, "Monthly" for 1 month)
 */
export const formatIntervalDisplay = (
  intervalCount: number,
  interval: string
): string => {
  // Show "Annual" for 12 months
  if (intervalCount === 12 && interval === 'month') {
    return 'Annual';
  }
  // Show "Monthly" for 1 month
  if (intervalCount === 1 && interval === 'month') {
    return 'Monthly';
  }
  // Fallback to original logic
  if (intervalCount === 1) {
    return `1 ${interval}`;
  }
  return `${intervalCount} ${interval}s`;
};

export const perCycleAmountBasedOnDuration = (
  amount: string | number,
  duration: string
): number => {
  if (duration.includes('12')) {
    return Math.floor(typeof amount === 'number' ? amount : parseFloat(amount));
  }

  if (
    duration.includes('3') ||
    duration.includes('2') ||
    duration.includes('6')
  ) {
    return Math.ceil(typeof amount === 'number' ? amount : parseFloat(amount));
  } else {
    return Number(amount);
  }
};

const getFacebookClickId = (): string | undefined => {
  try {
    if (typeof window === 'undefined') return undefined;

    const urlParams = new URLSearchParams(window.location.search);
    const fbclid = urlParams.get('fbclid');

    if (fbclid && fbclid.trim().length > 0) {
      return fbclid.trim();
    }

    // stored _fbc cookie as fallback
    if (typeof document !== 'undefined' && document.cookie) {
      const cookies = document.cookie.split(';');
      const fbcCookie = cookies.find(cookie =>
        cookie.trim().startsWith('_fbc=')
      );

      if (fbcCookie) {
        const value = fbcCookie.split('=')[1]?.trim();
        return value && value.length > 0 ? value : undefined;
      }
    }
  } catch (error) {
    console.warn('Error extracting Facebook Click ID:', error);
  }

  return undefined;
};

const getFacebookBrowserId = (): string | undefined => {
  try {
    if (typeof document === 'undefined' || !document.cookie) return undefined;

    const cookies = document.cookie.split(';');
    const fbpCookie = cookies.find(cookie => cookie.trim().startsWith('_fbp='));

    if (fbpCookie) {
      const value = fbpCookie.split('=')[1]?.trim();
      return value && value.length > 0 ? value : undefined;
    }
  } catch (error) {
    console.warn('Error extracting Facebook Browser ID:', error);
  }

  return undefined;
};

const formatPhoneNumber = (phone?: string): string | undefined => {
  if (!phone || typeof phone !== 'string') return undefined;

  // remove all non-numeric characters for Meta compliance
  const cleaned = phone.replace(/\D/g, '');

  return cleaned.length >= 7 ? cleaned : undefined;
};

const hashString = (input: string): string => {
  return CryptoJS.SHA256(input).toString();
};

const normalizeName = (name: string): string => {
  return name
    .toLowerCase()
    .replace(/[^\w\s]/g, '')
    .trim();
};

export const getEnhancedUserData = (user?: User): Record<string, string> => {
  try {
    const enhancedData: Record<string, string> = {};

    // add user data only if present and valid - hash PII according to Facebook guidelines
    if (
      user?.firstName &&
      typeof user.firstName === 'string' &&
      user.firstName.trim()
    ) {
      const normalizedFirstName = normalizeName(user.firstName.trim());
      enhancedData.first_name = hashString(normalizedFirstName);
    }

    if (
      user?.lastName &&
      typeof user.lastName === 'string' &&
      user.lastName.trim()
    ) {
      const normalizedLastName = normalizeName(user.lastName.trim());
      enhancedData.last_name = hashString(normalizedLastName);
    }

    if (user?.email && typeof user.email === 'string' && user.email.trim()) {
      const email = user.email.trim().toLowerCase();
      if (email.includes('@') && email.includes('.')) {
        enhancedData.email = email;
      }
    }

    const formattedPhone = formatPhoneNumber(user?.phoneNum);
    if (formattedPhone) {
      enhancedData.phone_number = hashString(formattedPhone);
    }

    const fbc = getFacebookClickId();
    if (fbc) {
      enhancedData.fbc = fbc;
    }

    const fbp = getFacebookBrowserId();
    if (fbp) {
      enhancedData.fbp = fbp;
    }

    return enhancedData;
  } catch (error) {
    console.warn('Error generating enhanced user data:', error);
    return {};
  }
};

// data and events tracking
export const getEcommerceDataProperties = (
  planId: string,
  planList: Plan[],
  properties?: Record<string, any>,
  addOnWeek: number = 1,
  user?: User
) => {
  const planDetail = planList.find(plan => plan.id === planId);

  const metaDataList = Object.values(
    planDetail?.mealPlanVariants ?? {}
  ) as MealPlanVariant[];
  const metaData = metaDataList[addOnWeek - 1];

  if (!planDetail) {
    return null;
  }

  // base e-commerce data object
  const baseData = {
    currency: 'AUD',
    value: Number(metaData.unitAmount),
    items: [
      {
        item_id: metaData.id,
        product_id: metaData.productId,
        item_name: planDetail.id,
        item_brand: 'Equalution',
        item_list_id:
          PLAN_NAME_OBJ[
            planDetail.intervalCount === 12 && planDetail.interval === 'month'
              ? '12 month'
              : `${planDetail.intervalCount} ${planDetail.interval}`
          ],
        item_list_name:
          PLAN_NAME_OBJ[
            planDetail.intervalCount === 12 && planDetail.interval === 'month'
              ? '12 month'
              : `${planDetail.intervalCount} ${planDetail.interval}`
          ],
        price: Number(metaData.unitAmount),
        quantity: 1,
      },
    ],
  };

  // add enhanced user data if user is provided
  const enhancedUserData = user ? getEnhancedUserData(user) : {};

  return {
    ...baseData,
    ...enhancedUserData,
    ...properties,
  };
};

export const calculateAge = (birthday: Date) => {
  // birthday is a date
  var ageDifMs = Date.now() - birthday.getTime();
  var ageDate = new Date(ageDifMs); // miliseconds from epoch
  return Math.abs(ageDate.getUTCFullYear() - 1970);
};

export const inchesToCentimeters = (inches: number): number => {
  return inches / 0.3937;
};

export const centimetersToFeetAndInches = (
  centimeters: number
): { feet: number; inches: number } => {
  const realFeet = (centimeters * 0.3937) / 12;
  const feet = Math.floor(realFeet);
  const inches = Math.round((realFeet - feet) * 12);
  return { feet: feet, inches: inches };
};

export const poundsToKilograms = (pounds: number): number => {
  return parseFloat((pounds / 2.2046).toFixed(2));
};
export const kiloToPounds = (kilo: number): number => {
  return parseFloat((kilo * 2.2046).toFixed(2));
};

export const handleEncodingURL = () => {
  const finalURL = window.location.href.replace('+', '%2B');
  window.location.href = finalURL;
};

export const FAT_CONSTANTS = {
  FEMALE_16_39: 0.2,
  FEMALE_40_59: 0.22,
  MALE_16_39: 0.18,
  MALE_40_59: 0.2,
};

export const calculateLBM = ({
  weight,
  height,
  gender,
}: {
  weight: number;
  height: number;
  gender: 'MALE' | 'FEMALE' | undefined;
}): number => {
  if (gender === 'MALE') {
    return 0.407 * weight + 0.267 * height - 19.2;
  }
  return 0.252 * weight + 0.473 * height - 48.3;
};
export type SuggestedWeightParams = {
  gender: 'MALE' | 'FEMALE' | undefined;
  age: number;
  lbm: number;
};

export const calculateSuggestedWeight = ({
  gender,
  age,
  lbm,
}: SuggestedWeightParams): number => {
  let fatConstant = 0;
  if (gender === 'FEMALE') {
    fatConstant =
      age <= 39 ? FAT_CONSTANTS.FEMALE_16_39 : FAT_CONSTANTS.FEMALE_40_59;
  } else if (gender === 'MALE') {
    fatConstant =
      age <= 39 ? FAT_CONSTANTS.MALE_16_39 : FAT_CONSTANTS.MALE_40_59;
  }
  return lbm / (1 - fatConstant);
};

export const isNumericOnly = (val: string) => {
  const re = new RegExp(/^\d*\.?\d*$/);
  return val ? re.test(val) : true;
};

export const hideIntercomElement = () => {
  const intercomElement: any = document.getElementsByClassName(
    'intercom-lightweight-app'
  )[0];
  if (intercomElement) {
    intercomElement.style.display = 'none';
  }
};

export const showIntercomElement = () => {
  const intercomElement: any = document.getElementsByClassName(
    'intercom-lightweight-app'
  )[0];
  if (intercomElement) {
    intercomElement.style.display = 'block';
  }
};

export const filterStipeErrorMessage = (error: string) => {
  if (!error) {
    return '';
  }
  return error.slice(error.indexOf(':') + 1);
};

export const checkNZUser = () => {
  return isUserFromNewZealand();
};

export const getCurrencySymbol = (
  currencyOrNZSymbol?: boolean | string,
  showGbpSymbol?: boolean,
  showAUSymbol?: boolean
) => {
  if (typeof currencyOrNZSymbol === 'string') {
    const cleanCurrency = currencyOrNZSymbol.toLowerCase();
    if (cleanCurrency === 'gbp' || cleanCurrency === 'uk') {
      return '£';
    }
    if (cleanCurrency === 'nzd' || cleanCurrency === 'nz') {
      return 'NZ$';
    }
    if (cleanCurrency === 'aud' || cleanCurrency === 'au') {
      return showAUSymbol ? 'AU$' : '$';
    }
    return '$';
  }

  if (typeof currencyOrNZSymbol === 'boolean') {
    if (showGbpSymbol) {
      return '£';
    }
    if (currencyOrNZSymbol) {
      return 'NZ$';
    }
    return showAUSymbol ? 'AU$' : '$';
  }

  const isUKUser = showGbpSymbol !== undefined ? showGbpSymbol : isUserFromUK();
  const isNewZealandUser = isUserFromNewZealand();

  if (isUKUser) {
    return '£';
  }

  if (isNewZealandUser) {
    return 'NZ$';
  }
  return showAUSymbol ? 'AU$' : '$';
};

export const detectActiveCurrency = (
  allSubscriptions?: any
): 'aud' | 'nzd' | 'gbp' | null => {
  if (!allSubscriptions) return null;

  const currencyOptions =
    allSubscriptions.currentSubscription?.currencyOptions ||
    allSubscriptions.trialSubscription?.currencyOptions ||
    allSubscriptions.upcomingSubscription?.currencyOptions;

  if (currencyOptions) {
    if (currencyOptions.gbp && currencyOptions.gbp.unit_amount > 0) {
      return 'gbp';
    }
    if (currencyOptions.nzd && currencyOptions.nzd.unit_amount > 0) {
      return 'nzd';
    }
    if (currencyOptions.aud && currencyOptions.aud.unit_amount > 0) {
      return 'aud';
    }

    const keys = Object.keys(currencyOptions);
    if (keys.length === 1) {
      const key = keys[0].toLowerCase();
      if (key === 'gbp' || key === 'nzd' || key === 'aud') {
        return key as 'aud' | 'nzd' | 'gbp';
      }
    }
  }

  return null;
};

// Discount calculation utilities
export interface Coupon {
  amountOff?: number;
  percentOff?: number;
}

export interface DiscountCalculation {
  originalTotal: number;
  discountedTotal: number;
  savings: number;
  discountAmount: number;
}

/**
 * Calculates discount details based on coupon and original total
 */
export const calculateDiscount = (
  originalTotal: number,
  coupon?: Coupon
): DiscountCalculation => {
  if (!coupon) {
    return {
      originalTotal,
      discountedTotal: originalTotal,
      savings: 0,
      discountAmount: 0,
    };
  }

  let discountAmount = 0;

  if (coupon.amountOff && coupon.amountOff > 0) {
    // Fixed amount discount (stored in cents)
    discountAmount = coupon.amountOff / 100;
  } else if (coupon.percentOff && coupon.percentOff > 0) {
    // Percentage discount
    discountAmount = originalTotal * (coupon.percentOff / 100);
  }

  const discountedTotal = Math.max(0, originalTotal - discountAmount);
  const savings = originalTotal - discountedTotal;

  return {
    originalTotal,
    discountedTotal,
    savings,
    discountAmount,
  };
};

export const isFreeTrialURL = () =>
  window.location.pathname.includes('/freetrial');

/**
 * Extracts a user-friendly error message from various error response formats
 */
export const extractErrorMessage = (
  error: unknown,
  defaultMessage: string = 'Something went wrong. Please try again.'
): string => {
  if (!error) {
    return defaultMessage;
  }

  const axiosError = error as AxiosError<any>;

  // Check if there's response data
  if (axiosError.response?.data) {
    const data = axiosError.response.data;

    // Handle string responses
    if (typeof data === 'string') {
      return data;
    }

    // Handle object responses with message property
    if (data.message) {
      return data.message;
    }

    // Handle object responses with error property
    if (data.error) {
      return data.error;
    }
  }

  // Fall back to axios error message
  if (axiosError.message) {
    return axiosError.message;
  }

  // Return default message
  return defaultMessage;
};
